import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api';

const LegalDocumentForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    documentType: 'terms_of_service',
    title: '',
    content: '',
    changeLog: '',
    incrementType: 'patch',
    effectiveDate: '',
    acceptanceEndDate: '', // New field
  });

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);

  const fetchDocument = useCallback(async () => {
    try {
      const response = await api.get(`/api/super-admin/legal-documents/${id}`);
      const doc = response.data.data;
      setFormData({
        documentType: doc.documentType,
        title: doc.title,
        content: doc.content,
        changeLog: doc.changeLog,
        incrementType: 'patch',
        effectiveDate: doc.effectiveDate ? doc.effectiveDate.split('T')[0] : '',
        acceptanceEndDate: doc.acceptanceEndDate ? doc.acceptanceEndDate.slice(0, 16) : '', // Format for datetime-local
      });
    } catch (error) {
      console.error('Error fetching document:', error);
      alert('Failed to fetch document');
      navigate('/super-admin/legal-documents');
    } finally {
      setFetching(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (isEdit) {
      fetchDocument();
    }
  }, [isEdit, fetchDocument]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEdit) {
        await api.put(`/api/super-admin/legal-documents/${id}`, {
          title: formData.title,
          content: formData.content,
          changeLog: formData.changeLog,
          effectiveDate: formData.effectiveDate,
          acceptanceEndDate: formData.acceptanceEndDate,
        });
        alert('Document updated successfully!');
      } else {
        await api.post('/api/super-admin/legal-documents', {
          documentType: formData.documentType,
          title: formData.title,
          content: formData.content,
          changeLog: formData.changeLog,
          incrementType: formData.incrementType,
          effectiveDate: formData.effectiveDate,
          acceptanceEndDate: formData.acceptanceEndDate,
        });
        alert('Document created successfully!');
      }
      navigate('/super-admin/legal-documents');
    } catch (error) {
      console.error('Error saving document:', error);
      alert(error.response?.data?.message || 'Failed to save document');
    } finally {
      setLoading(false);
    }
  };

  const documentTypes = [
    { value: 'terms_of_service', label: 'Terms of Service' },
    { value: 'privacy_policy', label: 'Privacy Policy' },
    { value: 'refund_policy', label: 'Refund Policy' },
    { value: 'cookie_policy', label: 'Cookie Policy' },
  ];

  const incrementTypes = [
    { value: 'major', label: 'Major (x.0.0) — Breaking changes' },
    { value: 'minor', label: 'Minor (1.x.0) — New features or sections' },
    { value: 'patch', label: 'Patch (1.0.x) — Clarifications & bug fixes' },
  ];

  // Get minimum date for acceptanceEndDate (must be at least today)
  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  };

  if (fetching) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-3 border-indigo-600 border-t-transparent"></div>
          <p className="mt-4 text-sm font-medium text-gray-500">Retrieving document details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800 antialiased p-4 sm:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        
        {/* Header Bar */}
        <div className="flex items-center justify-between pb-4 border-b border-gray-200">
          <div>
            <button
              onClick={() => navigate('/super-admin/legal-documents')}
              className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 transition-colors flex items-center gap-1 mb-2 focus:outline-none"
            >
              &larr; Back to Legal Management
            </button>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900">
              {isEdit ? 'Edit Legal Document' : 'Create Legal Document'}
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 mt-1 font-medium">
              {isEdit ? 'Update document body, change log, and compliance deadlines.' : 'Draft a new version for platform policy and tenant compliance.'}
            </p>
          </div>
        </div>

        {/* Form Card */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-slate-900 px-6 py-4 text-white border-b border-slate-800 flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-indigo-300">
              Document Parameters
            </span>
            <span className="text-xs text-slate-400">All fields marked with (*) are required</span>
          </div>

          <div className="p-6 sm:p-8 space-y-6">
            
            {/* Document Type - Only for new documents */}
            {!isEdit && (
              <div>
                <label className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-2">
                  Document Type *
                </label>
                <select
                  name="documentType"
                  value={formData.documentType}
                  onChange={handleChange}
                  required
                  className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all font-medium"
                >
                  {documentTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Title */}
            <div>
              <label className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-2">
                Document Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="e.g., Terms of Service v1.0.0"
                className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all font-medium"
              />
            </div>

            {/* Content Area */}
            <div>
              <label className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-2">
                Document Content (HTML Formatted) *
              </label>
              <textarea
                name="content"
                value={formData.content}
                onChange={handleChange}
                required
                rows={12}
                placeholder="Enter full legal text using standard HTML markup (e.g. <h2>, <p>, <ul>, <li>)..."
                className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-xs sm:text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all font-mono leading-relaxed"
              />
              <p className="mt-1.5 text-xs text-gray-500">
                HTML tags supported for structured document styling on the public pages.
              </p>
            </div>

            {/* Change Log */}
            <div>
              <label className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-2">
                Change Log / Revision Notes *
              </label>
              <textarea
                name="changeLog"
                value={formData.changeLog}
                onChange={handleChange}
                required
                rows={3}
                placeholder="Detail key changes introduced in this revision..."
                className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all"
              />
            </div>

            {/* Increment Type - Only for new documents */}
            {!isEdit && (
              <div>
                <label className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-2">
                  Version Increment Strategy *
                </label>
                <select
                  name="incrementType"
                  value={formData.incrementType}
                  onChange={handleChange}
                  className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all font-medium"
                >
                  {incrementTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Date Fields Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-2">
              
              {/* Effective Date */}
              <div>
                <label className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-2">
                  Effective Date
                </label>
                <input
                  type="date"
                  name="effectiveDate"
                  value={formData.effectiveDate}
                  onChange={handleChange}
                  className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all font-medium"
                />
                <p className="mt-1.5 text-xs text-gray-500">
                  Date when this document becomes active across platform views.
                </p>
              </div>

              {/* Acceptance End Date */}
              <div>
                <label className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-2">
                  Acceptance Deadline *
                </label>
                <input
                  type="datetime-local"
                  name="acceptanceEndDate"
                  value={formData.acceptanceEndDate}
                  onChange={handleChange}
                  required
                  min={getMinDateTime()}
                  className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all font-medium"
                />
                {formData.acceptanceEndDate && (
                  <p className="mt-1.5 text-xs text-emerald-700 font-semibold">
                    Deadline: {new Date(formData.acceptanceEndDate).toLocaleString('en-IN', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                )}
              </div>

            </div>

            {/* Notice Callout */}
            <div className="p-4 bg-amber-50 rounded-xl border border-amber-200 text-xs text-amber-900 space-y-1">
              <span className="font-bold uppercase tracking-wider block">Important Compliance Notice</span>
              <p className="text-amber-800 leading-relaxed">
                Tenants must acknowledge and accept this document by the specified deadline. Once the acceptance window expires, tenant access will be restricted until acceptance is granted.
              </p>
            </div>

            {/* Submission Action Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-end gap-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/super-admin/legal-documents')}
                className="w-full sm:w-auto px-6 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-800 font-medium text-sm rounded-xl transition-colors focus:outline-none"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-sm rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 shadow-xs"
              >
                {loading ? 'Saving...' : (isEdit ? 'Update Document' : 'Create Document')}
              </button>
            </div>

          </div>
        </form>

      </div>
    </div>
  );
};

export default LegalDocumentForm;